import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Loader2, Inbox, Users, Wallet, BarChart3, UserCog, CalendarDays, MessagesSquare, Medal, ShieldAlert, Megaphone, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import ContactsPanel from "@/components/admin/ContactsPanel";
import MembersPanel from "@/components/admin/MembersPanel";
import FinancePanel from "@/components/admin/FinancePanel";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import UsersPanel from "@/components/admin/UsersPanel";
import EventsPanel from "@/components/admin/EventsPanel";
import ChatModerationPanel from "@/components/admin/ChatModerationPanel";
import AwardsPanel from "@/components/admin/AwardsPanel";
import NotfallPanel from "@/components/admin/NotfallPanel";
import AnnounceModerationPanel from "@/components/admin/AnnounceModerationPanel";
import SecurityPanel from "@/components/admin/SecurityPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";

const formatError = (detail, fallback = "Etwas ist schiefgelaufen.") => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return fallback;
};

const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail, "Anmeldung fehlgeschlagen."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-anthracite px-6">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px]" style={{ background: "radial-gradient(ellipse, rgba(198,156,34,0.12) 0%, transparent 60%)" }} />
      <form onSubmit={submit} data-testid="admin-login-form" className="relative w-full max-w-md bg-anthracite-soft border border-gold/15 rounded-3xl p-8 lg:p-10 shadow-2xl shadow-black/40">
        <div className="flex items-center justify-center w-14 h-14 rounded-full border border-gold/40 mx-auto" style={{ background: "rgba(198,156,34,0.1)" }}>
          <Lock size={22} style={{ color: "#E5C76B" }} />
        </div>
        <h1 className="mt-6 text-center text-3xl font-semibold text-cream tracking-tight">Admin-Login</h1>
        <p className="mt-2 text-center text-sm text-cream/60">Brotherhood of Eden · Verwaltung</p>

        <label className="mt-8 flex flex-col gap-2">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/60">E-Mail</span>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" className="h-12 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60" />
        </label>
        <label className="mt-5 flex flex-col gap-2">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/60">Passwort</span>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" className="h-12 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60" />
        </label>

        <button type="submit" disabled={loading} data-testid="admin-login-submit" className="btn-primary mt-8 w-full disabled:opacity-70 disabled:cursor-not-allowed">
          {loading ? <><Loader2 size={18} className="animate-spin" /> Anmelden…</> : <>Anmelden</>}
        </button>
      </form>
    </div>
  );
};

const TABS = [
  { key: "contacts", label: "Anfragen", icon: Inbox },
  { key: "members", label: "Mitglieder", icon: Users },
  { key: "users", label: "Benutzer", icon: UserCog },
  { key: "awards", label: "Auszeichnungen", icon: Medal },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "chat", label: "Chat", icon: MessagesSquare },
  { key: "announce", label: "Ankündigungen", icon: Megaphone },
  { key: "finance", label: "Finanzen", icon: Wallet },
  { key: "analytics", label: "Analyse", icon: BarChart3 },
  { key: "security", label: "Sicherheit", icon: ShieldCheck },
  { key: "emergency", label: "Notfall", icon: ShieldAlert },
];

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("contacts");

  return (
    <div className="min-h-screen bg-anthracite text-cream">
      <header className="sticky top-0 z-10 bg-anthracite/90 backdrop-blur-xl border-b border-gold/15">
        <div className="container-boe flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-anthracite-soft border border-gold/40 flex items-center justify-center">
              <span className="font-accent italic text-xl font-semibold leading-none" style={{ color: "#E5C76B" }}>B</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-semibold text-cream tracking-tight text-sm">Admin · Verwaltung</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-cream/50">Brotherhood of Eden</span>
            </div>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-semibold rounded-full border border-gold/40 px-4 py-2 hover:bg-gold hover:text-anthracite transition-colors" style={{ color: "#E5C76B" }}>
            <LogOut size={16} /> Abmelden
          </button>
        </div>
        {/* Tabs */}
        <div className="container-boe">
          <div className="flex flex-wrap gap-x-1 gap-y-0 -mb-px">
            {TABS.map((tb) => {
              const Icon = tb.icon;
              const active = tab === tb.key;
              return (
                <button
                  key={tb.key}
                  onClick={() => setTab(tb.key)}
                  data-testid={`admin-tab-${tb.key}`}
                  className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    active ? "border-gold text-gold-soft" : "border-transparent text-cream/55 hover:text-cream"
                  }`}
                  style={active ? { color: "#E5C76B" } : undefined}
                >
                  <Icon size={16} /> {tb.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="container-boe py-12">
        {tab === "contacts" && <ContactsPanel onLogout={onLogout} />}
        {tab === "members" && <MembersPanel onLogout={onLogout} />}
        {tab === "users" && <UsersPanel onLogout={onLogout} />}
        {tab === "awards" && <AwardsPanel onLogout={onLogout} />}
        {tab === "events" && <EventsPanel onLogout={onLogout} />}
        {tab === "chat" && <ChatModerationPanel onLogout={onLogout} />}
        {tab === "announce" && <AnnounceModerationPanel onLogout={onLogout} />}
        {tab === "finance" && <FinancePanel onLogout={onLogout} />}
        {tab === "analytics" && <AnalyticsPanel onLogout={onLogout} />}
        {tab === "security" && <SecurityPanel onLogout={onLogout} />}
        {tab === "emergency" && <NotfallPanel onLogout={onLogout} />}
      </main>
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center bg-anthracite text-cream"><Loader2 size={28} className="animate-spin" style={{ color: "#E5C76B" }} /></div>;
  }

  return authed ? <Dashboard onLogout={logout} /> : <LoginForm onSuccess={() => setAuthed(true)} />;
};

export default Admin;
